import React, { useState } from 'react';
import PortalLayout from '@/components/PortalLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Question,
  Envelope,
  Phone,
  MapPin,
  CaretDown,
  CaretUp,
  Headset,
  Clock,
  ChatCircleDots,
} from '@phosphor-icons/react';

const FAQS = [
  {
    q: 'How do I submit a new service request?',
    a: 'Go to "New Request" in the top navigation. Select your company and service type, add any notes, upload the required documents, and submit. You\'ll be taken straight to the case page where you can track its progress.',
  },
  {
    q: 'What documents do I need to upload?',
    a: 'Required documents depend on the service type — most commonly your latest municipality bill and a copy of your ID. The form will show you exactly which documents are needed once you select a service type. Application forms (where required) will be supplied by Marema Consulting.',
  },
  {
    q: 'What file types and sizes are accepted?',
    a: 'We accept PDF, JPG, and PNG files up to 10 MB each. If your file is larger, please compress it or split it into multiple pages before uploading.',
  },
  {
    q: 'How long does a case typically take?',
    a: 'Service turnaround is 30–90 days depending on the type of service and the municipality involved. Your case status is updated on the dashboard at every step and you\'ll receive an email each time it changes.',
  },
  {
    q: 'What do the case statuses mean?',
    a: 'New Service Request → received and being reviewed. Quotation Accepted → awaiting site visit. Site Visit Scheduled → date confirmed. Query Logged → formally lodged with the municipality. Under Review → being processed. Approved / Completed → resolved.',
  },
  {
    q: 'How do I accept a quotation?',
    a: 'Open the "Quotations" tab. Click "Review" or "Open" on the quotation that needs your action, read the Terms & Conditions, then click Accept. A signed Terms of Service PDF will be emailed to you.',
  },
  {
    q: 'How do I update my profile or contact details?',
    a: 'Go to "Profile" in the top navigation. You can update your personal details and your company/account details. Your login email is fixed — contact support if you need to change it.',
  },
  {
    q: 'I\'ve forgotten my password — what now?',
    a: 'Use the "Forgot Password" link on the login page. We\'ll email you a reset link valid for 30 minutes.',
  },
  {
    q: 'Will I be notified when something changes on my case?',
    a: 'Yes. We send automatic emails when your case status changes, when a consultant adds a public note, and when a new document is uploaded to your case. Each email includes a direct link back to the case.',
  },
  {
    q: 'How do I get an invoice or quotation again?',
    a: 'All your quotations and invoices are in the "Quotations" tab. Click the eye icon to preview in your browser or the download icon to save a copy.',
  },
  {
    q: 'Is my information safe?',
    a: 'Yes. We handle your personal data in accordance with the Protection of Personal Information Act (POPIA). See the Privacy Policy on our website for details.',
  },
];

const FAQItem = ({ q, a, open, onClick, idx }) => (
  <div className="border border-gray-200" data-testid={`faq-item-${idx}`}>
    <button
      onClick={onClick}
      className="w-full flex items-start justify-between gap-4 p-4 text-left hover:bg-gray-50 transition-colors"
      data-testid={`faq-question-${idx}`}
    >
      <span className="font-medium text-[#0A192F]">{q}</span>
      {open ? (
        <CaretUp className="w-5 h-5 text-[#4CAF50] flex-shrink-0 mt-0.5" />
      ) : (
        <CaretDown className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
      )}
    </button>
    {open && (
      <div className="px-4 pb-4 text-sm text-gray-600 leading-relaxed" data-testid={`faq-answer-${idx}`}>
        {a}
      </div>
    )}
  </div>
);

const PortalHelp = () => {
  const [openIdx, setOpenIdx] = useState(0);

  return (
    <PortalLayout>
      <div className="max-w-4xl mx-auto space-y-8 fade-in">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Headset className="w-7 h-7 text-[#4CAF50]" weight="duotone" />
            <h1 className="text-2xl font-bold text-[#0A192F]">Help & Support</h1>
          </div>
          <p className="text-sm text-gray-500">
            Common questions about using your client portal. Can't find what you're looking for? Get in touch using the options below.
          </p>
        </div>

        {/* Contact Cards */}
        <div className="grid sm:grid-cols-3 gap-4">
          <a
            href="mailto:info@maremaconsulting.co.za"
            className="block border border-gray-200 p-5 hover:border-[#4CAF50] hover:bg-[#4CAF50]/5 transition-colors"
            data-testid="help-contact-email"
          >
            <Envelope className="w-6 h-6 text-[#4CAF50] mb-3" />
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Email us</p>
            <p className="text-sm font-medium text-[#0A192F] break-all">info@maremaconsulting.co.za</p>
          </a>
          <a
            href="tel:+27105009477"
            className="block border border-gray-200 p-5 hover:border-[#4CAF50] hover:bg-[#4CAF50]/5 transition-colors"
            data-testid="help-contact-phone"
          >
            <Phone className="w-6 h-6 text-[#4CAF50] mb-3" />
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Call us</p>
            <p className="text-sm font-medium text-[#0A192F]">+27 10 500 9477</p>
          </a>
          <div className="border border-gray-200 p-5">
            <Clock className="w-6 h-6 text-[#4CAF50] mb-3" />
            <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Office hours</p>
            <p className="text-sm font-medium text-[#0A192F]">Mon – Fri</p>
            <p className="text-xs text-gray-500 mt-0.5">08:00 – 17:00 SAST</p>
          </div>
        </div>

        {/* FAQ */}
        <Card>
          <CardHeader className="border-b border-gray-200 pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Question className="w-5 h-5 text-[#4CAF50]" /> Frequently Asked Questions
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="space-y-2" data-testid="faq-list">
              {FAQS.map((f, idx) => (
                <FAQItem
                  key={idx}
                  idx={idx}
                  q={f.q}
                  a={f.a}
                  open={openIdx === idx}
                  onClick={() => setOpenIdx(openIdx === idx ? -1 : idx)}
                />
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Office address */}
        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <MapPin className="w-6 h-6 text-[#4CAF50] flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-[#0A192F] mb-1">Visit our office</p>
                <p className="text-sm text-gray-600 leading-relaxed">
                  Office 555, 5th Floor, 288 on Kent<br />
                  288 Kent Avenue, Ferndale<br />
                  Randburg, Gauteng, 2194
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <Card className="bg-[#4CAF50] text-white">
          <CardContent className="p-8 text-center">
            <ChatCircleDots className="w-10 h-10 mx-auto mb-3" weight="duotone" />
            <h3 className="text-lg font-semibold mb-2">Still need help?</h3>
            <p className="text-sm opacity-90 mb-5">
              Our team is happy to answer any question about your case or our services.
            </p>
            <a href="mailto:info@maremaconsulting.co.za" data-testid="help-cta-email">
              <Button className="bg-white text-[#4CAF50] hover:bg-gray-100 rounded-none">
                <Envelope className="w-4 h-4 mr-2" /> Email Support
              </Button>
            </a>
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
};

export default PortalHelp;
